import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** GPM = Cv·√(ΔP/SG) — globe valve body, stem + handwheel, P1/P2 gauges, ΔP bracket. */
export function DiagramCvAndGPM() {
  const cy = 140;

  return (
    <DiagramCanvas>
      {/* Upstream pipe */}
      <Line x1={20} y1={cy} x2={130} y2={cy} stroke={DT.stroke} strokeWidth="10" strokeLinecap="square" />
      <Line x1={20} y1={cy} x2={130} y2={cy} stroke="rgba(122,170,187,0.4)" strokeWidth="6" strokeLinecap="square" />
      <Polygon points={`60,${cy} 50,${cy - 5} 50,${cy + 5}`} fill={DT.arrow} />

      {/* Valve body (globe) */}
      <Path d={`M130,${cy - 6} L130,${cy + 6} L145,${cy + 18} A22,22 0 0 0 205,${cy + 18} L220,${cy + 6} L220,${cy - 6} L205,${cy - 18} A22,22 0 0 0 145,${cy - 18} Z`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Seat + plug */}
      <Line x1={165} y1={cy} x2={185} y2={cy} stroke={DT.stroke} strokeWidth="1.4" />
      <Rect x={170} y={cy - 8} width={10} height={16} fill={DT.stroke} stroke={DT.stroke} strokeWidth="0.5" />
      {/* Stem */}
      <Line x1={175} y1={cy - 8} x2={175} y2={75} stroke={DT.stroke} strokeWidth="2.4" />
      {/* Handwheel */}
      <Circle cx={175} cy={70} r={14} fill="none" stroke={DT.stroke} strokeWidth="1.6" />
      <Circle cx={175} cy={70} r={4} fill={DT.stroke} />
      {[0, 45, 90, 135].map(d => {
        const r = d * Math.PI / 180;
        return <Line key={d} x1={175 - 14 * Math.cos(r)} y1={70 - 14 * Math.sin(r)}
          x2={175 + 14 * Math.cos(r)} y2={70 + 14 * Math.sin(r)} stroke={DT.stroke} strokeWidth="1" />;
      })}
      <SvgText x={175} y={54} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">handwheel</SvgText>

      {/* Downstream pipe */}
      <Line x1={220} y1={cy} x2={330} y2={cy} stroke={DT.stroke} strokeWidth="10" strokeLinecap="square" />
      <Line x1={220} y1={cy} x2={330} y2={cy} stroke="rgba(122,170,187,0.3)" strokeWidth="6" strokeLinecap="square" />
      <Polygon points={`300,${cy} 290,${cy - 5} 290,${cy + 5}`} fill={DT.arrow} />

      {/* P1 gauge (upstream) */}
      <Circle cx={75} cy={cy - 40} r={13} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      <SvgText x={75} y={cy - 36} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">P₁</SvgText>
      <Line x1={75} y1={cy - 27} x2={75} y2={cy - 5} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />

      {/* P2 gauge (downstream) */}
      <Circle cx={275} cy={cy - 40} r={13} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      <SvgText x={275} y={cy - 36} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">P₂</SvgText>
      <Line x1={275} y1={cy - 27} x2={275} y2={cy - 5} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,2" />

      {/* ΔP brace */}
      <Line x1={75} y1={cy - 62} x2={275} y2={cy - 62} stroke={DT.varColor} strokeWidth="1.2" />
      <Polygon points={`75,${cy - 62} 83,${cy - 66} 83,${cy - 58}`} fill={DT.varColor} />
      <Polygon points={`275,${cy - 62} 267,${cy - 66} 267,${cy - 58}`} fill={DT.varColor} />
      <SvgText x={175} y={cy - 66} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔP = P₁ − P₂</SvgText>

      {/* SG badge */}
      <Rect x={20} y={cy + 22} width={58} height={16} fill="rgba(0,0,0,0.4)" stroke={DT.dim} strokeWidth="0.6" />
      <SvgText x={49} y={cy + 33} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">SG (fluid)</SvgText>

      {/* Labels */}
      <LeaderLine x1={175} y1={cy + 22} x2={150} y2={215} varName="Cv" desc="valve coefficient" anchor="middle" />
      <LeaderLine x1={310} y1={cy} x2={325} y2={215} varName="GPM" desc="flow" anchor="end" />
      <LeaderLine x1={175} y1={cy - 66} x2={20} y2={40} varName="ΔP" desc="psi across valve" anchor="start" />

      <FormulaTag formula="GPM = Cv · √(ΔP / SG)   (Cv = 1 gpm per 1 psi on water)" />
    </DiagramCanvas>
  );
}
